import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  addGameSearchParams,
  checkForGameSearchParams,
  getGameSearchParams,
  removeGameSearchParams,
} from 'src/helpers/gameQueriesManager';
import { detectDevice, getCategoryName, getCategorySlug, getSkinId, setParams } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { gameUrlRequest, seacrhByGameNameRequest } from 'src/services/gameApi';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { setGameBoxWidgetClose, setSelectedCategory } from 'src/store/app/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setSelectedGame, setSelectedProvider, setSelectedTag } from 'src/store/games/actions';
import { forceCloseGameBox, setFreeSpinGames, setSelectedTounamentGame } from 'src/store/integrations/actions';
import { setBalance } from 'src/store/user/actions';
import { userInitialState } from 'src/store/user/reducer';
import { GameParamType } from 'src/types/req-dto/game';
import { RootState } from 'src/types/store-types';
import { GAMEBOX_WIDGET } from 'src/utils/constants';
import { useGDLIBLoad } from './use-gdlib-loadoad';
import { useSessionRecorder } from './use-session-recorder';

const useGameBox = (): void => {
  const sessionRecorder = useSessionRecorder();
  const { t } = useTranslation();
  const { locale } = useI18n();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const device = detectDevice();
  const { game, selectedTag } = useSelector((state: RootState) => state.games);
  const { user } = useSelector((state: RootState) => state.user);
  const { navigation } = useSelector((state: RootState) => state.configs);

  const skinId = getSkinId();
  const balanceRequestedRef = useRef<boolean>(false);
  // Tracks whether a game was playing recently so openGameFromGamebox can use
  // replace=true even when g_id was already cleared from the URL (e.g. after
  // CLOSE_GAME_BOX → closeGameBoxInGameWidget clears URL before next game opens).
  const lastGameRef = useRef<boolean>(false);

  const _openAuthModal = useConnectedAction(openAuthModal);
  const _openDialog = useConnectedAction(openDialog);
  const _requestBalance = useConnectedAction(setBalance.req);
  const _freeSpinGamesReq = useConnectedAction(setFreeSpinGames.req);
  const _forceCloseGameBox = useConnectedAction(forceCloseGameBox);
  const _selectedGame = useConnectedAction(setSelectedGame);
  const _setGameBoxWidgetClose = useConnectedAction(setGameBoxWidgetClose);
  const _setSelectedTounamentGame = useConnectedAction(setSelectedTounamentGame);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const IS_PLAYLIRA = process.env.REACT_APP_WEBSITENAME === 'PlatinumBet';

  const openGameFromSearchParams = useCallback((): void => {
    const { gameData, isEmpty } = getGameSearchParams(searchParams);

    if (!isEmpty && user) {
      try {
        _selectedGame(gameData);
        return;
      } catch (e) {
        console.error('Cannot launch game', e);
      }
    }

    if (!isEmpty && !user) {
      _openAuthModal('login');
      removeGameSearchParams(searchParams);
      setSearchParams(searchParams);
    }
  }, []);

  const deps = useMemo(() => (IS_PLAYLIRA ? [searchParams?.toString()] : []), [IS_PLAYLIRA, searchParams?.toString()]);

  const gameboxReady = (): void => {
    openGameFromSearchParams();
  };

  const closeGameBoxByGameInitiative = (): void => {
    if (balanceRequestedRef.current) return;
    balanceRequestedRef.current = true;
    _requestBalance();

    if (selectedTag === 'free_rounds' && userInitialState) {
      _freeSpinGamesReq();
    }
  };

  const getGameUrl = async (gameId: number, walletId: string): Promise<any> => {
    const params: GameParamType = setParams(user, { id: gameId }, skinId, locale, isMobile, walletId);

    try {
      balanceRequestedRef.current = false;
      return await gameUrlRequest(params);
    } catch (e: any) {
      const msg = t(`game_error.${e?.message === 'Network Error' ? 'network_error' : 'unavailable'}`, { gameId });
      console.log(msg);
      //   if (iframe) {
      //     iframe?.postMessage(
      //       {
      //         type: 'GAME_OPEN_FAILURE',
      //         gameId,
      //         msg,
      //       },
      //       '*'
      //     );
      //   }

      if (!e?.response?.data.success) {
        _forceCloseGameBox(true);
      }

      closeGameBoxByGameInitiative();
    }
  };

  const unsetStylesGameBox = (): void => {
    const iframe = document.getElementById(GAMEBOX_WIDGET);
    if (iframe) {
      iframe.style.cssText =
        'width: 0;height: 0; top: 0; position: fixed; border: none; right: 0;z-index: 150;transition: height 0.2s; display: block;';
    }
  };

  const closeGameBoxInGameWidget = (shouldNotifyGamebox = false): void => {
    const queryParams = new URLSearchParams(window?.location?.search);
    queryParams.delete('gameBox_category');
    removeGameSearchParams(queryParams);
    const queryString = queryParams.toString();
    const newPath = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    unsetStylesGameBox();
    document.body.removeAttribute('style');
    _selectedGame(null);
    const currentFullPath = window.location.pathname + window.location.search;
    if (currentFullPath !== newPath) {
      const categorySlug = newPath.match(/\/category\/([^/?]+)/)?.[1];
      // categorySlug is from the URL; selectedCategory must be the name (the data key)
      _setSelectedCategory(getCategoryName(navigation, categorySlug));
      navigate(newPath, { replace: true });
    }
    _setGameBoxWidgetClose();
    closeGameBoxByGameInitiative();
    shouldNotifyGamebox && window.__gdlib?.notify('closeGame');
    // Tell the race widget (if mounted) to refetch — the player's standing may have changed while playing.
    window.__gdlib?.notify('GD_REFRESH_RACE_ACTION');
  };

  const closeGameBoxLobbyWidget = (): void => {
    const gameboxSdkElement = document.querySelector('#__GAMEBOX__') as HTMLElement | null;
    if (gameboxSdkElement) gameboxSdkElement.style.display = 'none';
    unsetStylesGameBox();
    _setGameBoxWidgetClose();
  };

  const getGameUrlStr = (id: number | null = null, gameDataInfo: Record<string, string> = {}): void => {
    const gameId = id ?? +game?.id;
    const walletId = gameDataInfo?.gameWalletId ?? game?.gameWalletId;

    if (!id && Object.keys(gameDataInfo).length === 0) {
      window.__gdlib?.openGameBoxInGameWidget({
        parentId: GAMEBOX_WIDGET,
        type: 'openGameMode',
      });
    }

    getGameUrl(gameId, walletId).then((res) => {
      if (res?.success && user && locale) {
        const gameResponse = res.result.data;

        window.__gdlib?.openGameBoxInGameWidget({
          parentId: GAMEBOX_WIDGET, // maybe need to change parent id => parent name
          customClassName: 'frame',
          type: 'openGameMode',
          gameUrl: gameResponse.url,
          gameInfo: game || gameDataInfo,
        });
      } else {
        _openDialog({
          dialogType: EnumDialogsKeys.GAME_URL_FAILURE,
          dialogProps: { data: 'gameUrlFailure' },
        });
      }
    });
  };
  const openTournamentGame = (data: any): void => {
    navigate('/category/casino-tournament');
    _setSelectedTounamentGame(data);
    _setSelectedCategory('casinoTournament');
  };
  const openfreeRoundsPage = (): void => {
    navigate('/category/casino');
    _setSelectedCategory('casino');
  };
  const navigateToCategory = (payload: { category: string; params?: any }): void => {
    // payload.category may be a name or slug; selectedCategory must be the name (the data key)
    const categoryName = getCategoryName(navigation, payload.category);
    const categorySlug = getCategorySlug(navigation, categoryName);
    if (payload.params && !isNaN(payload.params)) {
      _setSelectedCategory(categoryName);
      localStorage.setItem('missionId', payload.params);
      _setSelectedTag('home');
      navigate(`/category/${categorySlug}`);
    } else if (payload.category) {
      _setSelectedProvider(['all_providers']);
      _setSelectedCategory(categoryName);
      _setSelectedTag('home');
      navigate(`/category/${categorySlug}`);
    }
  };

  useEffect(() => {
    const res = checkForGameSearchParams(searchParams);
    res ? openGameFromSearchParams() : closeGameBoxInGameWidget(true);
  }, deps);

  useEffect(() => {
    if (game) {
      lastGameRef.current = true;
    }
  }, [game]);

  useEffect(() => {
    const handlePopState = (): void => {
      const params = new URLSearchParams(window.location.search);
      if (!checkForGameSearchParams(params)) {
        // User navigated back to a clean URL — reset "game was playing" flag
        // so the next game opened from gamebox gets a fresh PUSH entry.
        lastGameRef.current = false;
        closeGameBoxInGameWidget(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (user && game) {
      if (game.openedByGamebox && game.gameUrl) {
        window.__gdlib?.openGameBoxInGameWidget({
          parentId: GAMEBOX_WIDGET,
          customClassName: 'frame',
          type: 'openGameMode',
          gameUrl: game.gameUrl,
          gameInfo: game,
        });
        return;
      }
      getGameUrlStr();
    }
  }, [user, game]);

  const closeOverlayWidgets = (): void => {
    const funMissionEl = document.getElementById('funMissionIframe');
    if (funMissionEl) funMissionEl.remove();
    const sdkEl = document.querySelector('#__GAMEBOX__') as HTMLElement | null;
    if (sdkEl) sdkEl.style.display = 'none';
  };

  const getGameInfoUrl = (id: number | null): void => {
    // Capture before SDK call — __gdlib may manipulate window.history, causing
    // React Router to lose track of the current pathname.
    const savedPathname = window.location.pathname;
    closeOverlayWidgets();
    window.__gdlib?.openGameBoxInGameWidget({ parentId: GAMEBOX_WIDGET });
    document.body.removeAttribute('style');
    _setGameBoxWidgetClose();

    seacrhByGameNameRequest(device, null, null, null, id)
      .then((gamesInfo) => {
        if (!gamesInfo?.success || !gamesInfo?.games?.length) return;

        const gameInfo = gamesInfo.games[0];
        // Use window.location.search directly — searchParams in this closure may be stale
        // (callback registered once on user change, URL may have changed since then)
        const currentParams = new URLSearchParams(window.location.search);
        if (currentParams.get('g_id') !== String(gameInfo.id)) {
          // Replace if already in a game (g_id in URL) OR if a game was recently
          // playing (lastGameRef) — handles the case where CLOSE_GAME_BOX clears
          // the URL before the next game is opened from the gamebox lobby.
          const shouldReplace = !!currentParams.get('g_id') || lastGameRef.current;
          addGameSearchParams(
            currentParams,
            (params, opts) => {
              const qs = params.toString();
              navigate(qs ? `${savedPathname}?${qs}` : savedPathname, opts);
            },
            gameInfo,
            shouldReplace
          );
        }
        _setSelectedCategory('home');
        _selectedGame(gameInfo);
      })
      .catch(() => {
        _openDialog({
          dialogType: EnumDialogsKeys.GAME_URL_FAILURE,
          dialogProps: { data: 'gameUrlFailure' },
        });
      });
  };

  const openGameFromGamebox = (payload: any): void => {
    if (!payload?.gameInfo) return;

    // Capture before SDK call — __gdlib may manipulate window.history, causing
    // React Router to lose track of the current pathname.
    const savedPathname = window.location.pathname;

    const gameInfo = {
      id: payload.gameInfo.gameId || payload.gameInfo.id,
      name: payload.gameInfo.gameName || payload.gameInfo.name || '',
      providerName: payload.gameInfo.providerName || payload.gameInfo.provider || '',
      ...payload.gameInfo,
    };

    closeOverlayWidgets();
    window.__gdlib?.openGameBoxInGameWidget({ parentId: GAMEBOX_WIDGET });
    document.body.removeAttribute('style');
    _setGameBoxWidgetClose();

    // Use window.location.search directly — searchParams in this closure may be stale
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.get('g_id') !== String(gameInfo.id)) {
      // Replace if already in a game (g_id in URL) OR if a game was recently
      // playing (lastGameRef) — handles the case where CLOSE_GAME_BOX clears
      // the URL before the next game is opened from the gamebox lobby.
      const shouldReplace = !!currentParams.get('g_id') || lastGameRef.current;
      addGameSearchParams(
        currentParams,
        (params, opts) => {
          const qs = params.toString();
          navigate(qs ? `${savedPathname}?${qs}` : savedPathname, opts);
        },
        gameInfo,
        shouldReplace
      );
    }
    _selectedGame(gameInfo);
  };

  const registerCallbacks = useCallback(() => {
    const gdlib = window.__gdlib!;
    gdlib.registerCallBack('gameBoxReady', 'useGamebox', gameboxReady);
    gdlib.registerCallBack('closeGameBoxInGameWidget', 'useGamebox', closeGameBoxInGameWidget);
    gdlib.registerCallBack('closeGameBoxLobbyWidget', 'useGamebox', closeGameBoxLobbyWidget);
    gdlib.registerCallBack('openTournamentGame', 'useGamebox', openTournamentGame);
    gdlib.registerCallBack('openfreeRoundsPage', 'useGamebox', openfreeRoundsPage);
    gdlib.registerCallBack('openDepositModal', 'useGamebox', () =>
      _openAccountModal({ section: 'deposit', subSection: 'deposit' })
    );
    gdlib.registerCallBack('funBonusSetGameUrl', 'useGamebox', (data: any) => {
      const gameData = {
        id: data.gameInfo?.gameId || data.gameInfo?.id,
        name: data.gameInfo?.gameName || data.gameInfo?.name || '',
        providerName: data.gameInfo?.providerName || data.gameInfo?.provider || '',
        ...data.gameInfo,
        gameUrl: data.gameUrl,
        openedByGamebox: true,
      };
      window.__gdlib?.openGameBoxInGameWidget({ parentId: GAMEBOX_WIDGET });
      document.body.removeAttribute('style');
      _setGameBoxWidgetClose();
      _selectedGame(gameData);
    });
    gdlib.registerCallBack('navigateToCategory', 'useGamebox', navigateToCategory);
    gdlib.registerCallBack(
      'gameBoxChangeCategoryTriggered',
      'useGamebox',
      (data: { navigation: 'vertical' | 'horizontal' }) => {
        data.navigation === 'vertical'
          ? sessionRecorder?.trackEvent('Gamebox vertical category change')
          : sessionRecorder?.trackEvent('Gamebox horizontal category change');
      }
    );
    gdlib.registerCallBack('gameBoxRaceRegistrationTriggered', 'useGamebox', () => {
      sessionRecorder?.trackEvent('Gamebox race registration');
    });
    gdlib.registerCallBack('gameBoxCasinoFreeSpinTriggered', 'useGamebox', () => {
      sessionRecorder?.trackEvent('Gamebox casino free spin click');
    });
    // navigation must be a dep: the SDK holds these callbacks, so they need re-registering when nav loads
  }, [navigation]);

  const checkGameInfoUrl = useCallback(() => {
    const gdlib = window.__gdlib!;

    if (user) {
      gdlib.registerCallBack('getGameInfoUrl', 'useGamebox', (id: number | null) => getGameInfoUrl(id));
      gdlib.registerCallBack('openGameFromGamebox', 'useGamebox', openGameFromGamebox);
    }
  }, [user]);

  useGDLIBLoad(registerCallbacks);
  useGDLIBLoad(checkGameInfoUrl);
};

export default useGameBox;
